import { useState, useRef, useEffect, useCallback } from 'react';

export const useAudioEngine = () => {
    const [isAudioLoaded, setIsAudioLoaded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const audioContextRef = useRef(null);
    const sourceNodesRef = useRef({});
    const gainNodesRef = useRef({});
    const stemsRef = useRef([]);
    const startTimeRef = useRef(0);
    const pauseTimeRef = useRef(0);
    const animationFrameRef = useRef(null);

    // Initialize Web Audio API context
    const initializeAudioContext = useCallback(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        return audioContextRef.current;
    }, []);

    // Create a silent fallback buffer (1 minute of silence)
    const createSilentBuffer = useCallback((audioContext, durationSecs = 60) => {
        const sampleRate = audioContext.sampleRate;
        return audioContext.createBuffer(1, sampleRate * durationSecs, sampleRate);
    }, []);

    // Load audio stems — fetches real audio files, falls back to silence if unavailable
    const loadStems = useCallback(async (stems) => {
        if (!stems || stems.length === 0) return;

        const audioContext = initializeAudioContext();
        stemsRef.current = stems;

        try {
            setIsAudioLoaded(false);

            const loadPromises = stems.map(async (stem) => {
                let audioBuffer;

                if (stem.url && stem.url.trim() !== '') {
                    try {
                        const response = await fetch(stem.url);
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status} fetching ${stem.url}`);
                        }
                        const arrayBuffer = await response.arrayBuffer();
                        audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                        console.log(`Loaded audio stem: ${stem.id} from ${stem.url}`);
                    } catch (fetchErr) {
                        // Graceful degradation — log and use silence so the rest of the
                        // practice experience still works even without audio files.
                        console.warn(
                            `Could not load audio for stem "${stem.id}" (${stem.url}): ${fetchErr.message}. Using silent fallback.`
                        );
                        audioBuffer = createSilentBuffer(audioContext);
                    }
                } else {
                    // No URL provided — silent placeholder
                    console.log(`No URL for stem "${stem.id}", using silent placeholder.`);
                    audioBuffer = createSilentBuffer(audioContext);
                }

                // Create gain node for this stem
                const gainNode = audioContext.createGain();
                gainNode.gain.value = stem.gain !== undefined ? stem.gain : 0.5;
                gainNode.connect(audioContext.destination);

                gainNodesRef.current[stem.id] = {
                    node: gainNode,
                    buffer: audioBuffer,
                    loop: stem.loop !== undefined ? stem.loop : false,
                };
            });

            await Promise.all(loadPromises);
            setIsAudioLoaded(true);

            // Use the longest buffer's duration, defaulting to 60s for silence
            const longestDuration = Object.values(gainNodesRef.current)
                .reduce((max, s) => Math.max(max, s.buffer.duration), 60);
            setDuration(longestDuration);

            console.log('All audio stems loaded successfully');
        } catch (error) {
            console.error('Error loading audio stems:', error);
        }
    }, [initializeAudioContext, createSilentBuffer]);

    // Update current time
    const updateTime = useCallback(() => {
        if (isPlaying && audioContextRef.current) {
            const elapsed = audioContextRef.current.currentTime - startTimeRef.current + pauseTimeRef.current;
            setCurrentTime(elapsed);
        }

        if (isPlaying) {
            animationFrameRef.current = requestAnimationFrame(updateTime);
        }
    }, [isPlaying]);

    // Play audio
    const play = useCallback(() => {
        if (!isAudioLoaded || !audioContextRef.current) return;

        const audioContext = audioContextRef.current;

        // Resume audio context if suspended (browser autoplay policy)
        if (audioContext.state === 'suspended') {
            audioContext.resume();
        }

        // Create and start source nodes for each stem
        Object.entries(gainNodesRef.current).forEach(([stemId, stemData]) => {
            if (sourceNodesRef.current[stemId]) {
                try {
                    sourceNodesRef.current[stemId].stop();
                } catch (_) {
                    // Already stopped — ignore
                }
            }

            const sourceNode = audioContext.createBufferSource();
            sourceNode.buffer = stemData.buffer;
            sourceNode.loop = stemData.loop;
            sourceNode.connect(stemData.node);

            sourceNode.start(0, pauseTimeRef.current % stemData.buffer.duration);
            sourceNodesRef.current[stemId] = sourceNode;
        });

        startTimeRef.current = audioContext.currentTime;
        setIsPlaying(true);

        // Start time updates
        updateTime();

        console.log('Audio playback started');
    }, [isAudioLoaded, updateTime]);

    // Pause audio
    const pause = useCallback(() => {
        if (!isPlaying || !audioContextRef.current) return;

        Object.values(sourceNodesRef.current).forEach(sourceNode => {
            if (sourceNode) {
                try {
                    sourceNode.stop();
                } catch (_) {
                    // Already stopped
                }
            }
        });

        pauseTimeRef.current = audioContextRef.current.currentTime - startTimeRef.current + pauseTimeRef.current;
        setIsPlaying(false);

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        console.log('Audio playback paused');
    }, [isPlaying]);

    // Stop audio
    const stop = useCallback(() => {
        if (!audioContextRef.current) return;

        Object.values(sourceNodesRef.current).forEach(sourceNode => {
            if (sourceNode) {
                try {
                    sourceNode.stop();
                } catch (_) {
                    // Already stopped
                }
            }
        });

        sourceNodesRef.current = {};
        pauseTimeRef.current = 0;
        startTimeRef.current = 0;
        setCurrentTime(0);
        setIsPlaying(false);

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        console.log('Audio playback stopped');
    }, []);

    // Set stem gain (volume)
    const setStemGain = useCallback((stemId, gain) => {
        const stemData = gainNodesRef.current[stemId];
        if (stemData && stemData.node) {
            stemData.node.gain.value = Math.max(0, Math.min(1, gain));
            console.log(`Set ${stemId} gain to ${gain}`);
        }
    }, []);

    // Get current audio time
    const getCurrentTime = useCallback(() => {
        if (!audioContextRef.current) return 0;

        if (isPlaying) {
            return audioContextRef.current.currentTime - startTimeRef.current + pauseTimeRef.current;
        } else {
            return pauseTimeRef.current;
        }
    }, [isPlaying]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stop();
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [stop]);

    // Pause when tab becomes hidden to save resources
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && isPlaying) {
                pause();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isPlaying, pause]);

    return {
        isAudioLoaded,
        isPlaying,
        currentTime,
        duration,
        loadStems,
        play,
        pause,
        stop,
        setStemGain,
        getCurrentTime,
        formatTime: useCallback((time) => {
            const minutes = Math.floor(time / 60);
            const seconds = Math.floor(time % 60);
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }, [])
    };
};
